<script>
  // Authoring surface for the private cubes. Two callers, deliberately one
  // component: the Disclosure step edits a draft in the browser and commits it on
  // Confirm, while Queries mints one aggregate at a time straight into the Pod.
  // What differs between them is where the work lands, not what the analyst does.
  //
  //   rows      sample CSV rows — present in Disclosure, absent in Queries, which
  //             is why previewing has to be able to go to the Pod instead.
  //   onsave    mint immediately (Queries). Absent → mutate the draft.
  //   onremove  drop an already-saved aggregate (Queries). Absent → splice.
  //   onpreview (aggregate|null) → { summaries, candidate }, computed over the
  //             private records. Only used when there are no sample rows.
  import Panel from './Panel.svelte';
  import Button from './Button.svelte';
  import Alert from './Alert.svelte';
  import { markDirty } from '../lib/disclosureDraft.svelte.js';
  import {
    projectRowsForAggregation, dimensionEligibility, eligibleDimensions, combineProblem,
    missingMarginals, suggestAggregateId, validateAggregateId, themesInUse, removalProblem,
  } from '../../../lib/disclosure_core.js';
  import { summarizeAggregates } from '../../../lib/aggregate_core.js';

  let {
    mapping, aggregateMapping = mapping, sourceId = null, aggregations, rows = [],
    onsave = null, onremove = null, onpreview = null,
    saveLabel = 'Add cube',
  } = $props();

  // Only the Disclosure draft has anything to dirty. Queries edits the Pod copy
  // directly, so marking the draft here would put "Not saved yet" on a Disclosure
  // flow the analyst never opened.
  const touch = () => { if (!onsave) markDirty(); };

  const eligible = $derived(eligibleDimensions(mapping));
  const ineligible = $derived(dimensionEligibility(mapping).filter((d) => !d.eligible));
  const marginals = $derived(missingMarginals(mapping, aggregations));
  const themes = $derived(themesInUse(aggregations));

  // Sample rows make every summary free and immediate; without them the Pod is
  // the only place that knows, and it has to be asked.
  const records = $derived(rows.length ? projectRowsForAggregation(mapping, rows) : null);
  const local = $derived(records ? summarizeAggregates(records, aggregations) : null);
  let fetched = $state({});
  const summaries = $derived(local ?? fetched);

  const threshold = $derived(aggregations.suppressBelow);
  const thresholdProblem = $derived(
    Number.isInteger(threshold) && threshold >= 0 ? null : 'Hide-under must be a whole number, 0 or more.',
  );

  // ── The candidate ─────────────────────────────────────────────────────────
  let first = $state('');
  let second = $state('');
  let customId = $state('');
  let themeChoice = $state(null);
  const theme = $derived(themeChoice ?? themes[0] ?? '');

  const dimensions = $derived([first, second].filter(Boolean));
  const kind = $derived(dimensions.length === 2 ? 'combined' : 'single');
  const suggestedId = $derived(suggestAggregateId(dimensions));
  const aggregateId = $derived(customId.trim() || suggestedId);

  const shapeProblem = $derived.by(() => {
    if (!first) return 'Choose a dimension to break down by.';
    if (!second) return null;
    return combineProblem(eligible.find((d) => d.target === first), eligible.find((d) => d.target === second));
  });
  const idProblem = $derived(first ? validateAggregateId(aggregateId, aggregations) : null);
  const themeProblem = $derived(first && !theme ? 'Give the cube a theme — it is what governance grants by.' : null);
  const problem = $derived(shapeProblem ?? idProblem ?? themeProblem ?? thresholdProblem);

  const candidate = $derived(problem ? null
    : { id: aggregateId, kind, theme, dimensions, sharing: { mode: 'none' } });

  // A preview describes one exact proposal. Change the proposal and the numbers
  // on screen stop being about it, so Add is only offered while they still are.
  const signature = $derived(JSON.stringify([dimensions, threshold]));
  let previewed = $state(null);
  let previewSignature = $state(null);
  const fresh = $derived(Boolean(previewed) && previewSignature === signature);

  let previewing = $state(false);
  let saving = $state(false);
  let error = $state(null);

  async function ask(aggregate) {
    if (records) {
      const one = aggregate ? summarizeAggregates(records, { ...aggregations, aggregates: [aggregate] }) : {};
      return { summaries: local, candidate: aggregate ? one[aggregate.id] : null };
    }
    if (!onpreview) throw new Error('Load a CSV sample to preview.');
    return onpreview(aggregate);
  }

  async function preview() {
    previewing = true; error = null;
    try {
      const result = await ask(candidate);
      if (result?.summaries) fetched = result.summaries;
      previewed = result?.candidate ?? null;
      previewSignature = signature;
    } catch (e) {
      error = e.message; previewed = null;
    } finally {
      previewing = false;
    }
  }

  async function showExisting() {
    previewing = true; error = null;
    try { fetched = (await ask(null))?.summaries ?? {}; }
    catch (e) { error = e.message; }
    finally { previewing = false; }
  }

  async function add() {
    if (!candidate || !fresh) return;
    saving = true; error = null;
    try {
      if (onsave) await onsave(candidate);
      else { aggregations.aggregates.push(candidate); touch(); }
      first = ''; second = ''; customId = ''; previewed = null; previewSignature = null;
    } catch (e) {
      error = e.message;
    } finally {
      saving = false;
    }
  }

  // Fills the form from a suggested marginal and previews it in one gesture. The
  // offer is a shortcut into the same authoring path, never a second way to add.
  async function setUp(target) {
    first = target; second = ''; customId = ''; previewed = null;
    await preview();
  }

  // Kept apart from the form's own error: a failed removal belongs with the
  // table it happened in, not under a proposal the analyst may be mid-way
  // through composing.
  let removeError = $state(null);
  async function remove(index) {
    const aggregate = aggregations.aggregates[index];
    // The button is not offered for a shared aggregate; this is the same rule
    // said again, for the case where the config changed under the screen.
    removeError = removalProblem(aggregate);
    if (removeError) return;
    try {
      if (onremove) await onremove(aggregate);
      else { aggregations.aggregates.splice(index, 1); touch(); }
    } catch (e) { removeError = e.message; }
  }

  const published = (summary) => {
    if (!summary) return null;
    const shown = summary.examples.map((e) => `${e.label} ${e.count}`).join(' · ');
    const more = summary.published - summary.examples.length;
    return more > 0 ? `${shown} · +${more} more` : shown || 'nothing';
  };
</script>

<Panel>
  <div class="wf2-row" style="gap:12px">
    <label class="wf2-field" style="min-width:180px">Hide any count under
      <input class="wf2-control" type="number" min="0" bind:value={aggregations.suppressBelow} oninput={touch} />
    </label>
    <p class="wf2-caption">Applied to every cube. Counts below this threshold stay private.</p>
  </div>
  {#if thresholdProblem}
    <p class="wf2-hint">{thresholdProblem}</p>
  {/if}

  <!-- Three columns, because the cube's identity is one thing and not four: its
       name, the dimensions it is made of, its theme and its kind all answer
       "which aggregate is this", and `kind` is already legible in the dimensions
       (one, or two with a plus). What the analyst is actually reading across is
       what gets published, so that column gets the room. -->
  <div class="wf2-tablewrap" style="overflow-x:auto; margin-top:16px">
    <table class="wf2-table">
      <thead><tr>
        <th style="width:30%">Cube</th>
        <th style="width:52%">What gets published</th>
        <th>Sharing</th><th></th>
      </tr></thead>
      <tbody>
        {#each aggregations.aggregates as a, i (a.id)}
          {@const summary = summaries[a.id]}
          <tr>
            <td>
              <span class="mono">{a.id}</span>
              {#if a.theme}<span class="tag {a.theme}" style="margin-left:8px">{a.theme}</span>{/if}
              <span class="col-stats mono">{a.dimensions.join(' + ')}</span>
              <span class="col-stats">institution-wide registry</span>
            </td>
            <td>
              {#if summary}
                {published(summary)}
                <span class="col-stats">
                  {summary.published} of {summary.possible} groups · {summary.hidden} hidden under {threshold}
                </span>
              {:else}
                <span class="muted">not previewed</span>
              {/if}
            </td>
            <td>{a.sharing?.mode ?? 'none'}</td>
            <td style="white-space:nowrap">
              {#if !removalProblem(a)}<Button variant="ghost" onclick={() => remove(i)}>Remove</Button>{/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  <!-- Said once under the table rather than in every shared row: it is the same
       sentence each time, and repeating it crowded out the counts. -->
  {#if aggregations.aggregates.some((a) => removalProblem(a))}
    <p class="wf2-caption">
      Cubes governance already shares cannot be removed here — withdraw them on Partners first.
    </p>
  {/if}
  {#if removeError}
    <Alert title="Could not remove" variant="danger"><p>{removeError}</p></Alert>
  {/if}

  {#if !local && onpreview}
    <div class="wf2-row" style="gap:12px; margin-top:12px">
      <Button variant="ghost" onclick={showExisting} disabled={previewing}>
        {previewing ? 'Counting…' : 'Show what gets published'}
      </Button>
      <p class="wf2-caption">Counted over the records already in your Pod. Nothing is written.</p>
    </div>
  {/if}
</Panel>

{#if marginals.length}
  <Panel>
    <h3 class="wf2-panel-title">Breakdowns you do not have yet</h3>
    <p class="wf2-text">
      Every labelled column can be counted on its own. These have no breakdown yet
      — set one up to see what it would publish. Nothing is added until you add it.
    </p>
    <div class="wf2-tablewrap" style="overflow-x:auto">
      <table class="wf2-table">
        <thead><tr><th>Break down by</th><th>From column</th><th>Treatment</th><th></th></tr></thead>
        <tbody>
          {#each marginals as d (d.target)}
            <tr>
              <td class="mono">{d.target}</td>
              <td>{d.source}</td>
              <td>{d.treatment}</td>
              <td><Button variant="ghost" onclick={() => setUp(d.target)}>Set up and preview</Button></td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  </Panel>
{/if}

<Panel>
  <h3 class="wf2-panel-title">Add a cube</h3>
  {#if sourceId}<p class="wf2-caption">Authoring from source <span class="mono">{sourceId}</span>. Only dimensions emitted by this source are offered.</p>{/if}
  <p class="wf2-text">
    One dimension counts it on its own; adding a second crosses the two. See what
    it would publish before you add it — a cube can be safe and still say almost
    nothing once small groups are hidden. New cubes stay private until
    governance explicitly publishes them.
  </p>

  <div class="wf2-row" style="gap:12px; flex-wrap:wrap">
    <label class="wf2-field" style="min-width:220px">Break down by
      <select class="wf2-control" bind:value={first}>
        <option value="">Choose…</option>
        {#each eligible as d}<option value={d.target}>{d.target}</option>{/each}
      </select>
    </label>
    <label class="wf2-field" style="min-width:220px">Crossed with (optional)
      <select class="wf2-control" bind:value={second}>
        <option value="">— nothing, count it on its own —</option>
        {#each eligible as d}<option value={d.target}>{d.target}</option>{/each}
      </select>
    </label>
    <label class="wf2-field" style="min-width:180px">Theme
      {#if themes.length}
        <select class="wf2-control" value={theme} onchange={(e) => (themeChoice = e.currentTarget.value)}>
          {#each themes as t}<option value={t}>{t}</option>{/each}
        </select>
      {:else}
        <input class="wf2-control" value={theme} oninput={(e) => (themeChoice = e.currentTarget.value)} placeholder="e.g. diversity" />
      {/if}
    </label>
    <label class="wf2-field" style="min-width:200px">Cube id
      <input class="wf2-control mono" bind:value={customId} placeholder={suggestedId || 'generated from the dimensions'} />
    </label>
  </div>
  <p class="wf2-caption">
    Saved as <span class="mono">kind: {kind}</span>{#if aggregateId} · <span class="mono">{aggregateId}</span>{/if}.
    Theme is how governance grants access, so it groups this cube with everything else under that name.
  </p>

  <div class="wf2-row" style="gap:12px; margin-top:12px">
    <Button variant="secondary" onclick={preview} disabled={Boolean(problem) || previewing}>
      {previewing ? 'Counting…' : 'Preview'}
    </Button>
    <Button variant="primary" onclick={add} disabled={!fresh || saving}>
      {saving ? 'Computing…' : saveLabel}
    </Button>
  </div>
  {#if problem}
    <p class="wf2-hint">{problem}</p>
  {:else if !fresh}
    <p class="wf2-hint">Preview it first — the numbers are the decision.</p>
  {/if}

  {#if error}
    <Alert title="Cannot add cube" variant="danger"><p>{error}</p></Alert>
  {/if}

  {#if fresh && previewed}
    <div class="wf2-tablewrap" style="overflow-x:auto; margin-top:16px">
      <table class="wf2-table">
        <thead><tr>
          <th>Possible groups</th><th>Hidden (under {threshold})</th><th>Published</th><th>What gets published</th>
        </tr></thead>
        <tbody><tr>
          <td>{previewed.possible}</td>
          <td>{previewed.hidden}</td>
          <td>{previewed.published} ({previewed.lossPct}% hidden)</td>
          <td>{published(previewed)}</td>
        </tr></tbody>
      </table>
    </div>
    <Alert title={previewed.verdict} variant={previewed.variant}>
      <p class="wf2-text">
        Everything published is a group of {threshold}+, so it is safe to share.
        {#if previewed.lossPct >= 50}
          But {previewed.lossPct}% of the groups are too small to appear, so what
          you publish here describes the larger groups only — anyone reading it
          would be reading about part of your records, not all of them. Two
          separate breakdowns, one per column, usually keep far more.
        {/if}
      </p>
    </Alert>
  {/if}

  {#if ineligible.length}
    <details class="drop-list" style="margin-top:16px">
      <summary>{ineligible.length} labelled {ineligible.length === 1 ? 'column is' : 'columns are'} not offered here</summary>
      <div style="padding: var(--space-2) var(--space-3)">
        <ul class="wf2-text">
          {#each ineligible as d}
            <li><span class="mono">{d.target || d.source}</span> — {d.reason}.</li>
          {/each}
        </ul>
        <p class="wf2-caption">
          Change any of these on Assess and label; a column becomes available here as soon as its labelling allows it.
        </p>
      </div>
    </details>
  {/if}
</Panel>
